import React from 'react';
import { ChevronDown, Info, MousePointerClick, Trash2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { ActionButton, ActionButtonModel } from '@/store/videoFeatures';
import { actionButtonLabel, actionButtonSummary } from '@/lib/action-button/list';
import { BelowVideoTab } from './tabs/BelowVideoTab';
import { OverVideoTab } from './tabs/OverVideoTab';
import { CustomHtmlTab } from './tabs/CustomHtmlTab';

const TABS = [
  { value: 'abaixo-video', label: 'Abaixo do Vídeo' },
  { value: 'sobre-video', label: 'Sobre o Vídeo' },
  { value: 'html', label: 'HTML Personalizado' },
] as const;

interface ActionButtonCardProps {
  button: ActionButton;
  index: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onChange: (key: string, value: unknown) => void;
  onTemplateChange: (key: string, value: unknown) => void;
  onSelectModel: (model: ActionButtonModel) => void;
  onRemove: () => void;
}

/** Um botão da lista: cabeçalho com resumo, chave e lixeira; aberto, as abas de sempre. */
export const ActionButtonCard: React.FC<ActionButtonCardProps> = ({
  button,
  index,
  expanded,
  onToggleExpanded,
  onChange,
  onTemplateChange,
  onSelectModel,
  onRemove,
}) => {
  const label = actionButtonLabel(index);
  // Trocar de aba só troca o tipo: o resto da configuração continua no botão.
  const buttonType = button.buttonType || 'abaixo-video';

  return (
    <div data-action-button-card className="rounded-xl border border-[#E4E7EC] bg-white overflow-hidden">
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="flex-1 flex items-center gap-3 min-w-0 text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-[#F8F9FB] border border-[#E4E7EC] flex-shrink-0 flex items-center justify-center">
            <MousePointerClick className="w-4 h-4 text-[#9CA3AF]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#1A1A1A] truncate">{label}</p>
            <p className="text-[11px] text-[#6B7280] truncate">{actionButtonSummary(button)}</p>
          </div>
          <ChevronDown
            className={cn('w-4 h-4 text-[#9CA3AF] transition-transform flex-shrink-0', expanded && 'rotate-180')}
          />
        </button>
        <Switch
          checked={button.enabled === true}
          onCheckedChange={(checked) => onChange('enabled', checked)}
          aria-label={`Ligar ${label}`}
        />
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remover ${label}`}
          title={`Remover ${label}`}
          className="h-8 w-8 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all flex-shrink-0"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-[#EEF0F3] p-4 space-y-5 bg-[#FCFCFD]">
          <div className="grid grid-cols-3 h-[44px] bg-white border border-[#E4E7EC] rounded-lg overflow-hidden">
            {TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => onChange('buttonType', tab.value)}
                className={cn(
                  'flex-1 text-[13px] font-medium transition-all duration-200',
                  buttonType === tab.value
                    ? 'bg-[#1F6BFF] text-white font-semibold'
                    : 'text-muted-foreground hover:bg-[#1F6BFF]/10 hover:text-[#1F6BFF]',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {buttonType === 'abaixo-video' && (
            <div className="flex items-center gap-2 p-3 bg-[#F0F7FF] border border-[#D4E5FF] rounded-lg">
              <Info className="h-4 w-4 text-[#1F6BFF] flex-shrink-0" />
              <span className="text-xs text-[#1C1C1E]">
                Para que as alterações do botão de ação abaixo do vídeo funcionem, você precisa copiar o código do
                vídeo depois de salvar e colar no seu site.
              </span>
            </div>
          )}

          {buttonType === 'abaixo-video' && (
            <BelowVideoTab
              actionButton={button}
              updateActionButton={onChange}
              updateTemplate={onTemplateChange}
              onSelectModel={onSelectModel}
            />
          )}
          {buttonType === 'sobre-video' && (
            <OverVideoTab
              actionButton={button}
              updateActionButton={onChange}
              updateTemplate={onTemplateChange}
              onSelectModel={onSelectModel}
            />
          )}
          {buttonType === 'html' && <CustomHtmlTab actionButton={button} updateActionButton={onChange} />}
        </div>
      )}
    </div>
  );
};
